import {
  CheckOutlined,
  CloseOutlined,
  PaperClipOutlined,
  PlusOutlined
} from "@ant-design/icons"
import { App, Button, DatePicker, Flex, Form, Select, Tooltip } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { type ReactNode, useMemo, useState } from "react"

import { MODAL_CONFIRM_CONFIG } from "../../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { CHECKLIST_COLUMN_KEYS } from "./columns.tsx"
import { buildEditRowCells, rowCommitKeyDown } from "../../../../../../../../components/EntityTable/editable-row-cells.ts"
import { FormChangedWatcher } from "../../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { useAppSelector } from "../../../../../../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../../../../../../hooks/use-date-format.ts"
import { dateAutoFill } from "../../../../../../../../utils/form/date-auto-fill.tsx"
import { isFormChanged } from "../../../../../../../../utils/object/is-form-changed.ts"
import { SubcontractorFormModal } from "../../../../../../../SubcontractorsPage/components/SubcontractorForm/SubcontractorFormModal.tsx"
import type { SubcontractorFormData } from "../../../../../../../SubcontractorsPage/components/SubcontractorForm/subcontractor-form-data.ts"
import type { SubcontractorListItem } from "../../../../../../../SubcontractorsPage/types/types.ts"
import type { ProjectChecklistItem } from "../../../../../../types/project-checklists.types.tsx"
import { ProjectViewChecklistsAssignedSelect } from "./ProjectViewChecklistsAssignedSelect.tsx"
import { ProjectViewChecklistsNewRowMilestones } from "./ProjectViewChecklistsNewRowMilestones.tsx"
import { TextArea } from "../../../../../../../../components/Input/TextArea.tsx"

const excludeGeneric = (o: BaseOptionType) => o?.value !== "generic"

interface ProjectViewChecklistsMaxNewRowProps {
  // rc-table hands the default cells to a body-row component; the edit row
  // matches its own cells against them by column key.
  children?: ReactNode
  record: ProjectChecklistItem
  mode?: "create" | "edit"
  showStatusCheckbox?: boolean
  addSubcontractorAllowed?: boolean
  addNewAddSubcontractor: (
    data: SubcontractorFormData
  ) => Promise<SubcontractorListItem | null>
  onSelectRow: (
    record: ProjectChecklistItem,
    action: string
  ) => Promise<void> | void
}

export const ProjectViewChecklistsMaxNewRow = ({
  record,
  onSelectRow,
  addSubcontractorAllowed,
  mode = "create",
  showStatusCheckbox,
  addNewAddSubcontractor,
  ...rest
}: ProjectViewChecklistsMaxNewRowProps) => {
  const { getDateValueProps, createDateRangeRules, normalizeDate } =
    useDateFormat()
  const options = useAppSelector((s) => s?.project?.checklistItemsFilterOptions)

  const [showAddSubcontractor, setShowAddSubcontractor] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectChecklistItem>()
  const { modal } = App.useApp()

  const isCreate = mode === "create"
  const projectTypesOptions = useMemo(
    () => options?.["project_type"]?.filter(excludeGeneric),
    [options?.["project_type"]]
  )

  const projectTypeMilestonesMap = useMemo((): Map<
    string,
    BaseOptionType[]
  > => {
    return new Map(
      projectTypesOptions?.map((item) => [
        item?.value,
        item?.milestones?.filter(excludeGeneric)
      ]) ?? []
    )
  }, [projectTypesOptions])

  const toggleShowAddSubcontractor = () =>
    setShowAddSubcontractor(!showAddSubcontractor)

  const handleSave = async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue()

      const action = isCreate ? "create" : "update"

      setSaving(true)

      await onSelectRow(
        {
          ...record,
          ...values,
          start_date: normalizeDate(values?.start_date),
          end_date: normalizeDate(values?.end_date)
        },
        action
      )
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const action = isCreate ? "cancel_create" : "cancel"

    const isChanged = isFormChanged<ProjectChecklistItem>(
      form.getFieldsValue(),
      record
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, action)
        }
      })
    } else {
      onSelectRow(record, action)
    }
  }

  const handleNewAddSubcontractor = async (data: SubcontractorFormData) => {
    try {
      const subcontractor = await addNewAddSubcontractor(data)
      if (!subcontractor) return

      form.setFieldValue("assigned_to", subcontractor?.contact_user)
      setShowAddSubcontractor(false)
    } catch (e) {
      console.log(e)
    }
  }

  const rowClassName = isCreate ? "bg-blue-50" : ""

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    busy: saving
  })

  return (
    <Form<ProjectChecklistItem>
      form={form}
      component={false}
      initialValues={record}
      disabled={saving}
    >
      {showAddSubcontractor && (
        <SubcontractorFormModal
          onSave={handleNewAddSubcontractor}
          onCancel={toggleShowAddSubcontractor}
        />
      )}

      <FormChangedWatcher<ProjectChecklistItem> form={form} saving={saving} />

      <tr {...rest} onKeyDown={onKeyDownRow} className={rowClassName}>
        {buildEditRowCells(
          rest?.children,
          Object.values(CHECKLIST_COLUMN_KEYS),
          {
            [CHECKLIST_COLUMN_KEYS.isDone]: <td className="ant-table-cell" />,
            [CHECKLIST_COLUMN_KEYS.name]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectChecklistItem>
                  name="name"
                  noStyle
                  rules={[
                    { required: true, message: "Required" },
                    { max: 255, message: "Must be at least 255 characters" }
                  ]}
                >
                  <TextArea
                    autoSize
                    placeholder="Enter task name"
                    autoFocus={isCreate}
                  />
                </Form.Item>
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.projectTypeDisplay]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectChecklistItem>
                  name="project_type"
                  noStyle
                  normalize={(value: string) => value || null}
                  getValueFromEvent={(
                    value: ProjectChecklistItem["project_type"]
                  ) => {
                    form.setFieldValue("project_phase", null)
                    return value
                  }}
                >
                  <Select
                    className="w-full"
                    placeholder="Select type"
                    options={projectTypesOptions}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                  />
                </Form.Item>
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.projectPhaseName]: (
              <td className="ant-table-cell">
                <ProjectViewChecklistsNewRowMilestones
                  form={form}
                  options={options}
                  projectTypeMilestonesMap={projectTypeMilestonesMap}
                />
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.priorityDisplay]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectChecklistItem> name="priority" noStyle>
                  <Select
                    className="w-full"
                    placeholder="Priority"
                    options={options?.priority || []}
                  />
                </Form.Item>
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.assignedToName]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectChecklistItem>
                  name="assigned_to"
                  noStyle
                  normalize={(value: string) => value || null}
                >
                  <ProjectViewChecklistsAssignedSelect
                    className="w-full"
                    options={options}
                    spreedOption
                    onAddClick={
                      addSubcontractorAllowed
                        ? toggleShowAddSubcontractor
                        : undefined
                    }
                  />
                </Form.Item>
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.startDate]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectChecklistItem>
                  name="start_date"
                  noStyle
                  getValueProps={getDateValueProps}
                  dependencies={["end_date"]}
                  rules={createDateRangeRules<ProjectChecklistItem>(
                    "end_date",
                    "start"
                  )}
                >
                  <DatePicker
                    className="w-full"
                    onChange={dateAutoFill<ProjectChecklistItem>({
                      form,
                      endDatePath: ["end_date"]
                    })}
                  />
                </Form.Item>
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.endDate]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectChecklistItem>
                  name="end_date"
                  noStyle
                  getValueProps={getDateValueProps}
                  dependencies={["start_date"]}
                  rules={createDateRangeRules<ProjectChecklistItem>(
                    "start_date",
                    "end"
                  )}
                >
                  <DatePicker className="w-full" />
                </Form.Item>
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.status]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectChecklistItem> name="status" noStyle>
                  <Select
                    className="w-full"
                    placeholder="Select status"
                    options={options?.status || []}
                  />
                </Form.Item>
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.attachments]: (
              <td className="ant-table-cell text-center">
                <Tooltip title="Only available in view mode">
                  <Button disabled icon={<PaperClipOutlined />} />
                </Tooltip>
              </td>
            )
          },
          <td
            className={`sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first ${rowClassName}`}
          >
            <Flex gap="small" justify="center">
              <Button
                variant="outlined"
                color="danger"
                onClick={handleCancel}
                icon={<CloseOutlined />}
              />
              <Button
                loading={saving}
                variant="outlined"
                color="green"
                onClick={handleSave}
                icon={isCreate ? <PlusOutlined /> : <CheckOutlined />}
              />
            </Flex>
          </td>
        )}
      </tr>
    </Form>
  )
}
